/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Brian Bal, Slobodan Djordjevic
 */
import React, { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { valueFor, getShouldShowTraceTeam } from "../../util/helpers"
import * as links from "../../util/links"
import { Row, Link, Color, pageTitle, TraceTeamInfoCard } from "trux"
import {
    Modal,
    ModalTitle,
    ContentSection,
    TitleSection,
    CloseButton,
    Label,
    Stack,
} from "trux"
import {
    SensorlessResultsContent,
    YourTracerMissingContent,
} from "../../components/MissingSensors"

import "./ChoosePlaylist.css"

const ChoosePlaylist = (props) => {
    const dispatch = useDispatch()
    const teamId = props.match.params.team_name
    const gameId = props.match.params.game_id
    const allPlaylistOverride = "_all_"
    const allowedFamilyPlaylists = [allPlaylistOverride]
    let goalCount = 0
    const sportType = useSelector((state) => state.game?.value?.sport_type)
    const teamPlaylists = useSelector((state) => {
        let lists = valueFor("game.value.playlists", [], state)
        let progList = lists.find((l) => l.id === "progression")
        let progsList = lists.find((l) => l.id === "my-progression")
        let homeGoals = lists.find((l) => l.id === "home-goals")
        if (homeGoals && homeGoals.event_count !== goalCount) {
            goalCount = homeGoals.event_count
        }

        if (
            progsList &&
            progList &&
            progList.event_count > 0 &&
            progsList.event_count > 0
        ) {
            progList.enabled = false
        }
        return lists.filter(
            (p) =>
                (p.enabled === true || p.show_when_empty) &&
                !p.scope.player &&
                !p.id.startsWith("camera-")
        )
    })
    const playerPlaylists = useSelector((state) => {
        let lists = valueFor("game.value.playlists", [], state)
        return lists.filter(
            (p) => (p.enabled === true || p.show_when_empty) && p.scope.player
        )
    })

    const sensorlessMessage = useSelector((state) => {
        let game = valueFor("game.value", "none", state)
        let user = valueFor("user.value", null, state)
        let showGame = game && game.video_render_type === "sensorless"
        let showUser = false
        let userOnTeam = false
        let userHasStats = false

        if (user && game && game.home_team && game.home_team.players) {
            for (let player of game.home_team.players) {
                if (user.user_id === player.user.user_id) {
                    userOnTeam = true
                    break
                }
            }

            if (userOnTeam) {
                let stats = valueFor("player_stats.value", null, state)
                if (stats && stats.game) {
                    for (let stat of stats.game) {
                        if (
                            user &&
                            stat?.athlete?.user?.user_id === user.user_id
                        ) {
                            userHasStats = true
                            break
                        }
                    }
                }
            }
        }

        if (userOnTeam && !userHasStats) {
            showGame = false
            showUser = true
        }

        return { showGame, showUser }
    })

    const close = (e) => {
        if (e) {
            e.preventDefault()
        }
        props.history.push(props.location.pathname.replace("/choose", ""))
    }

    const handleShowTraceTeamDialog = () => {
        props.history.push(props.location.pathname + "/upgrade-trace-team")
    }

    const clearFilters = () => {
        dispatch({
            type: "SET_FILTER",
            key: "selectedCamera",
            value: "all",
        })
        dispatch({
            type: "SET_FILTER",
            key: "selectedHalf",
            value: 0,
        })
    }

    // eslint-disable-next-line eqeqeq
    const open = links.getUrlExtras(window.location.hash).choose != null

    useEffect(() => {
        if (open) {
            pageTitle("Choose Playlist", "Choose Playlist")
        }
    }, [open])

    // QUESTION: Should this be here?
    for (let playlist of teamPlaylists) {
        if (playlist.id === "home-goals") {
            goalCount = playlist.event_count
            break
        }
    }

    // Trace Family vs Trace Team
    const user = useSelector((state) => state.user.value)
    const userDivisions = useSelector((state) => state.divisions.value)
    const game = useSelector((state) => state.game.value)
    const team = useSelector((state) => state.team.value)
    let showTraceTeamInfoCard = getShouldShowTraceTeam(
        game,
        user,
        userDivisions
    )
    let isTraceFamilyTeam = team && team.division?.type === "family"
    let playerRankingClass = isTraceFamilyTeam ? "rank-one" : "rank-two"
    let tacticalRankingClass = isTraceFamilyTeam ? "rank-two" : "rank-one"
    const isPanoramicSensorless =
        !game ||
        (game &&
            game.video_render_type === "panoramic_sensorless_global_identity")

    const shouldShowPlaylist = (title) => {
        return (
            !isTraceFamilyTeam ||
            allowedFamilyPlaylists.includes(allPlaylistOverride) ||
            allowedFamilyPlaylists.includes(title.toLowerCase)
        )
    }

    return (
        <Modal
            trkContext="Choose Playlist"
            open={open}
            onShadeClick={close}
            className="ChoosePlaylist"
        >
            <TitleSection onClose={close}>
                <ModalTitle>Game Playlists</ModalTitle>
                <CloseButton onClick={close} />
            </TitleSection>
            <ContentSection>
                {sensorlessMessage.showGame && <SensorlessResultsContent />}

                {sensorlessMessage.showUser && <YourTracerMissingContent />}

                <Stack type={Stack.Type.Row} xs={Stack.Type.Column}>
                    <Stack
                        className={tacticalRankingClass}
                        type={Stack.Type.Column}
                        span={6}
                    >
                        <Label
                            type={Label.Type.Heading5}
                            color={Color.Grey1}
                            marginBottom="2"
                        >
                            Tactical Highlights
                        </Label>
                        <ul>
                            {teamPlaylists.map(
                                (p) =>
                                    shouldShowPlaylist(p.title) && (
                                        <li
                                            key={`chooseplaylist-item-${p.id}`}
                                            onClick={clearFilters}
                                        >
                                            {p.event_count > 0 && (
                                                <Link
                                                    data-trk-act={`${p.title} Pressed`}
                                                    className="text-semi-bold"
                                                    to={links.gameUrl(
                                                        teamId,
                                                        p.id,
                                                        gameId
                                                    )}
                                                >
                                                    {p.title} &ndash;{" "}
                                                    {p.event_count}
                                                </Link>
                                            )}
                                            {p.event_count === 0 && (
                                                <Label color={Color.Grey3} semi>
                                                    {p.title} &ndash; 0
                                                </Label>
                                            )}
                                        </li>
                                    )
                            )}
                        </ul>
                    </Stack>

                    <Stack
                        className={playerRankingClass}
                        type={Stack.Type.Column}
                        span={6}
                    >
                        <Label
                            type={Label.Type.Heading5}
                            color={Color.Grey1}
                            marginBottom="2"
                        >
                            Player Highlights
                        </Label>
                        {playerPlaylists.length > 0 && (
                            <ul>
                                {playerPlaylists.map((p) => {
                                    let title = ""
                                    if (
                                        p.event_count - goalCount <= 0 &&
                                        p.title &&
                                        p.title.includes("Tracer")
                                    ) {
                                        return null
                                    }
                                    if (
                                        p.scope.player &&
                                        p.scope.player.jersey_number !== ""
                                    ) {
                                        title = `#${p.scope.player.jersey_number} ${p.title}`
                                    } else {
                                        title = p.title
                                    }

                                    if (
                                        p.scope.player &&
                                        p.scope.player.jersey_number !== ""
                                    ) {
                                        title = `#${p.scope.player.jersey_number} ${p.title}`
                                    } else {
                                        title = p.title
                                    }

                                    return (
                                        <li
                                            key={`chooseplaylist-item-${p.id}`}
                                            onClick={clearFilters}
                                        >
                                            {p.event_count - goalCount > 0 && (
                                                <Link
                                                    data-trk-act="Player Playlist Pressed"
                                                    className="text-semi-bold"
                                                    to={links.gameUrl(
                                                        teamId,
                                                        p.id,
                                                        gameId
                                                    )}
                                                >
                                                    <Row alignItems="center">
                                                        {sportType ===
                                                            "baseball" && (
                                                            <Label
                                                                className="players-position"
                                                                spacingTop="1"
                                                                spacingBottom="1"
                                                                spacingLeft="2"
                                                                spacingRight="2"
                                                                marginRight="3"
                                                                semi
                                                                content={
                                                                    p.positions
                                                                        ?.length
                                                                        ? p
                                                                              .positions[0]
                                                                        : "-"
                                                                }
                                                            />
                                                        )}
                                                        {title} &ndash;{" "}
                                                        {p.event_count}
                                                    </Row>
                                                </Link>
                                            )}
                                            {p.event_count - goalCount <= 0 && (
                                                <Row alignItems="center">
                                                    {sportType ===
                                                        "baseball" && (
                                                        <Label
                                                            className="players-position color-gray"
                                                            spacingTop="1"
                                                            spacingBottom="1"
                                                            spacingLeft="2"
                                                            spacingRight="2"
                                                            marginRight="3"
                                                            semi
                                                            content={"-"}
                                                            color={Color.Grey3}
                                                        />
                                                    )}
                                                    <Label
                                                        color={Color.Grey3}
                                                        semi
                                                    >
                                                        {title} &ndash; {0}
                                                    </Label>
                                                </Row>
                                            )}
                                        </li>
                                    )
                                })}
                            </ul>
                        )}
                        {playerPlaylists.length === 0 && (
                            <Label color={Color.Grey3} marginBottom="4">
                                Roster not created yet.
                            </Label>
                        )}
                    </Stack>
                </Stack>
                {showTraceTeamInfoCard && (
                    <TraceTeamInfoCard
                        renderAsRow={false}
                        onButtonClick={(e) => handleShowTraceTeamDialog(e)}
                    />
                )}
                {!isPanoramicSensorless && (
                    <Stack className="how-playlists-created">
                        <h3>How are Playlists Created?</h3>
                        <p>
                            TRACE-BOT collects all the video and sensor data
                            then cuts playlists automatically - so you don’t
                            have to.
                        </p>
                    </Stack>
                )}
            </ContentSection>
        </Modal>
    )
}

export default ChoosePlaylist
